import BaseController from "./base_controller"
import { t } from "@/utils/i18n"
import type {
  Chart,
  ChartConfiguration,
  ChartType,
  ChartData,
  ChartOptions
} from 'chart.js'

// Chart.js is loaded dynamically to reduce initial bundle size
// This saves ~80KB on pages that don't use charts (homepage, protocols, substances)
let ChartJS: typeof Chart | null = null
const DOSE_CORRELATION_BAR_VARIANT = 'dose-correlation-bar'
const DOSE_CORRELATION_RADAR_VARIANT = 'dose-correlation-radar'

async function loadChartJS(): Promise<typeof Chart> {
  if (ChartJS) return ChartJS

  const [
    { Chart, LineController, BarController, RadarController, CategoryScale, LinearScale, PointElement, LineElement, BarElement, RadialLinearScale, Filler, Tooltip, Legend },
    { MatrixController, MatrixElement }
  ] = await Promise.all([
    import('chart.js'),
    import('chartjs-chart-matrix')
  ])

  // Register Chart.js components
  Chart.register(
    LineController,
    BarController,
    RadarController,
    MatrixController,
    MatrixElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    RadialLinearScale,
    Filler,
    Tooltip,
    Legend
  )

  ChartJS = Chart
  return Chart
}

// Connects to data-controller="charts"
export default class extends BaseController {
  static targets = ["canvas"]
  static values = {
    type: String,
    data: Object,
    options: Object,
    variant: String
  }

  declare readonly canvasTarget: HTMLCanvasElement
  declare readonly hasCanvasTarget: boolean
  declare typeValue: ChartType
  declare dataValue: ChartData
  declare optionsValue: ChartOptions
  declare variantValue: string
  declare hasOptionsValue: boolean
  declare hasVariantValue: boolean

  private chart: Chart | null = null

  connect(): void {
    super.connect()
    this.debug('connect', {
      hasCanvas: this.hasCanvasTarget,
      type: this.typeValue,
      hasData: !!this.dataValue
    })
    this.initializeChart()
  }

  disconnect(): void {
    super.disconnect()
    this.destroyChart()
  }

  // Initialize Chart.js instance
  private async initializeChart(): Promise<void> {
    if (!this.hasCanvasTarget) {
      this.debug('No canvas target found')
      return
    }

    if (!this.dataValue) {
      this.debug('No data provided for chart')
      return
    }

    this.destroyChart() // Cleanup existing instance

    try {
      // Load Chart.js dynamically
      const Chart = await loadChartJS()

      const ctx = this.canvasTarget.getContext('2d')
      if (!ctx) {
        this.handleError(new Error('Could not get canvas context'), 'initializeChart')
        return
      }

      const config: ChartConfiguration = {
        type: this.typeValue || 'line',
        data: this.prepareData(),
        options: this.mergeOptions()
      }

      this.chart = new Chart(ctx, config)
      this.debug('Chart initialized', { type: config.type })
    } catch (error) {
      this.handleError(error, 'initializeChart')
    }
  }

  // Prepare data with special handling for Matrix charts
  private prepareData(): ChartData {
    if (this.typeValue === 'matrix' && this.dataValue.datasets) {
      // Add backgroundColor function for matrix charts
      const data = { ...this.dataValue }
      data.datasets = data.datasets.map((dataset: any) => ({
        ...dataset,
        backgroundColor: (context: any) => {
          const value = context.raw?.v
          if (value === null || value === undefined) {
            return 'rgba(200, 200, 200, 0.3)'
          }

          // Color gradient based on correlation value
          if (value >= 0) {
            // Positive correlation: green (stronger = darker)
            const opacity = 0.3 + (value * 0.6)
            return `rgba(16, 185, 129, ${opacity})`
          } else {
            // Negative correlation: red (stronger = darker)
            const opacity = 0.3 + (Math.abs(value) * 0.6)
            return `rgba(239, 68, 68, ${opacity})`
          }
        },
        width: ({ chart }: any) => {
          const area = chart.chartArea || {}
          const xLabels = (this.dataValue.labels as any)?.x || []
          return (area.width || 0) / xLabels.length - 1
        },
        height: ({ chart }: any) => {
          const area = chart.chartArea || {}
          const yLabels = (this.dataValue.labels as any)?.y || []
          return (area.height || 0) / yLabels.length - 1
        }
      }))
      return data
    }

    return this.dataValue
  }

  // Destroy Chart.js instance to prevent memory leaks
  private destroyChart(): void {
    if (this.chart) {
      this.debug('Destroying chart')
      this.chart.destroy()
      this.chart = null
    }
  }

  // Merge default options with provided options
  private mergeOptions(): ChartOptions {
    if (this.isMatrixChart()) {
      return this.getMatrixOptions()
    }

    if (this.usesRadarOptions()) {
      return this.getRadarOptions()
    }

    if (this.usesHorizontalBarOptions()) {
      return this.getHorizontalBarOptions()
    }

    const defaults: ChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 15,
            font: {
              size: 12
            }
          }
        },
        tooltip: {
          enabled: true,
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          padding: 12,
          cornerRadius: 4,
          titleFont: {
            size: 14,
            weight: 'bold'
          },
          bodyFont: {
            size: 13
          },
          callbacks: {
            label: (context) => {
              let label = context.dataset.label || ''
              if (label) {
                label += ': '
              }
              if (context.parsed.y !== null) {
                label += context.parsed.y.toFixed(1)
              }
              return label
            }
          }
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      },
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            maxRotation: 45,
            minRotation: 0
          }
        },
        y: {
          beginAtZero: true,
          max: 5,
          ticks: {
            stepSize: 1
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.05)'
          }
        }
      }
    }

    return this.hasOptionsValue ? this.deepMerge(defaults, this.optionsValue) : defaults
  }

  // Special options for Matrix (heatmap) charts
  private getMatrixOptions(): ChartOptions {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: true,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          padding: 12,
          cornerRadius: 4,
          callbacks: {
            title: () => '',
            label: (context: any) => {
              const v = context.raw.v
              return `${context.raw.y} × ${context.raw.x}: ${v ? v.toFixed(2) : 'N/A'}`
            }
          }
        }
      },
      scales: {
        x: {
          type: 'category',
          labels: (this.dataValue.labels as any)?.x || [],
          offset: true,
          grid: {
            display: false
          }
        },
        y: {
          type: 'category',
          labels: (this.dataValue.labels as any)?.y || [],
          offset: true,
          grid: {
            display: false
          }
        }
      }
    } as any
  }

  private isMatrixChart(): boolean {
    return this.typeValue === 'matrix'
  }

  private usesRadarOptions(): boolean {
    return this.typeValue === 'radar' || this.variantValue === DOSE_CORRELATION_RADAR_VARIANT
  }

  private usesHorizontalBarOptions(): boolean {
    return this.variantValue === DOSE_CORRELATION_BAR_VARIANT
  }

  // Special options for Radar charts (dose correlation)
  private getRadarOptions(): ChartOptions {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: true,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          padding: 12,
          cornerRadius: 4,
          callbacks: {
            label: (context: any) => {
              return `${context.label}: ${context.parsed.r.toFixed(2)}`
            }
          }
        }
      },
      scales: {
        r: {
          beginAtZero: true,
          min: 0,
          max: 1,
          ticks: {
            stepSize: 0.2,
            callback: function(value: any) {
              return value.toFixed(1)
            }
          },
          pointLabels: {
            font: {
              size: 13,
              weight: 'bold'
            }
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)'
          },
          angleLines: {
            color: 'rgba(0, 0, 0, 0.1)'
          }
        }
      }
    } as any
  }

  // Special options for Horizontal Bar charts (dose correlation)
  private getHorizontalBarOptions(): ChartOptions {
    return {
      indexAxis: 'y' as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: true,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          padding: 12,
          cornerRadius: 4,
          callbacks: {
            label: (context: any) => {
              const value = context.parsed.x
              let interpretation = ''

              if (value >= 0.5) {
                interpretation = t('strong_positive')
              } else if (value >= 0.2) {
                interpretation = t('moderate_positive')
              } else if (value >= -0.2) {
                interpretation = t('no_relationship')
              } else if (value >= -0.5) {
                interpretation = t('moderate_negative')
              } else {
                interpretation = t('strong_negative')
              }

              return `${value >= 0 ? '+' : ''}${value.toFixed(2)}${interpretation}`
            }
          }
        }
      },
      scales: {
        x: {
          min: -1,
          max: 1,
          ticks: {
            stepSize: 0.25,
            callback: function(value: any) {
              return value >= 0 ? `+${value}` : value
            }
          },
          grid: {
            color: (context: any) => {
              return context.tick.value === 0 ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)'
            },
            lineWidth: (context: any) => {
              return context.tick.value === 0 ? 2 : 1
            }
          }
        },
        y: {
          grid: {
            display: false
          }
        }
      }
    } as any
  }

  // Deep merge helper for nested objects
  private deepMerge(target: any, source: any): any {
    const output = { ...target }

    if (this.isObject(target) && this.isObject(source)) {
      Object.keys(source).forEach(key => {
        if (this.isObject(source[key])) {
          if (!(key in target)) {
            Object.assign(output, { [key]: source[key] })
          } else {
            output[key] = this.deepMerge(target[key], source[key])
          }
        } else {
          Object.assign(output, { [key]: source[key] })
        }
      })
    }

    return output
  }

  private isObject(item: any): boolean {
    return item && typeof item === 'object' && !Array.isArray(item)
  }

  // Public methods for external control
  updateChart(newData: ChartData): void {
    if (!this.chart) {
      this.debug('Chart not initialized, cannot update')
      return
    }

    this.chart.data = newData
    this.chart.update('none') // 'none' for instant update, or 'active' for animation
    this.debug('Chart updated')
  }

  refreshChart(): void {
    this.initializeChart()
  }

  toggleDataset(index: number): void {
    if (!this.chart) return

    const meta = this.chart.getDatasetMeta(index)
    if (meta) {
      meta.hidden = !meta.hidden
      this.chart.update()
      this.debug('Dataset toggled', { index, hidden: meta.hidden })
    }
  }

  // Stimulus action to handle dataset visibility toggle from legend
  handleLegendClick(event: Event): void {
    const target = event.target as HTMLElement
    const index = parseInt(target.dataset.index || '0', 10)
    this.toggleDataset(index)
  }
}
